<template>
  <v-card class="pa-4">
    <h2>Outstanding Master List</h2>
    <v-row dense>
      <v-col cols="12" md="3">
        <v-menu v-model="menuDate" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="selectedDate"
              label="As Of Date"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              required
            />
          </template>
          <v-date-picker
            v-model="selectedDate"
            type="date"
            @change="menuDate = false"
          />
        </v-menu>
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="selectedDept"
          :items="deptOptions"
          item-text="name"
          item-value="id"
          label="Department"
          dense
          clearable
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="selectedSubDept"
          :items="subDeptOptions"
          item-text="subCode"
          item-value="id"
          label="File Classification"
          dense
          clearable
        />
      </v-col>
      <v-col cols="12" md="3" class="d-flex align-end">
        <v-btn
          color="primary"
          @click="generateTable"
          :disabled="!selectedDate"
          :loading="loading"
          class="mr-2"
        >
          <v-icon left>mdi-table</v-icon>
          Generate Table
        </v-btn>
        <v-btn
          color="success"
          @click="exportExcel"
          :disabled="!tableData.length"
          :loading="exporting"
        >
          <v-icon left>mdi-file-excel</v-icon>
          Export Excel
        </v-btn>
      </v-col>
    </v-row>
    <v-alert type="info" outlined class="mt-4">
      The exported Excel file will contain the outstanding master list as of
      your selected date and filters.
    </v-alert>

    <v-divider class="my-4"></v-divider>

    <!-- Summary Header (Date-based) -->
    <div v-if="tableData.length" class="mb-4">
      <v-card flat class="pa-2" style="background: #e8f5e9">
        <div
          style="
            background: #388e3c;
            color: #fff;
            font-family: Tahoma;
            font-size: 1.1rem;
            font-weight: bold;
            text-align: center;
            padding: 8px;
            border-radius: 4px;
          "
        >
          {{ summaryTitle }}
        </div>
        <div
          style="
            background: #f5f5f5;
            color: #333;
            font-family: Tahoma;
            font-size: 0.95rem;
            text-align: center;
            padding: 6px;
            border-radius: 4px;
          "
        >
          Outstanding Master List,
          <span style="font-weight: bold">
            {{ deptLabel || "All Departments" }}
          </span>
          ,
          <span style="font-weight: bold">
            {{ subDeptLabel || "All Classifications" }}
          </span>
        </div>
        <div
          style="
            background: #fffbe6;
            color: #856404;
            font-family: Tahoma;
            font-size: 0.95rem;
            text-align: center;
            padding: 6px;
            border-radius: 4px;
            margin-top: 6px;
          "
        >
          For detailed breakdown by branch and classification, please click
          <strong>Export to Excel</strong>.
        </div>
      </v-card>
    </div>

    <!-- Summary Cards/Chips (Vuetify 2 style, conditional display) -->
    <v-row v-if="tableData.length" class="mb-4">
      <!-- Show TOTAL RECORDS only if ALL DEPT and ALL CLASS -->
      <v-col
        cols="12"
        md="2"
        v-if="selectedDept === ALL_DEPT && selectedSubDept === ALL_CLASS"
      >
        <v-card flat class="pa-2" color="#1976d2" dark>
          <div class="d-flex align-center">
            <v-icon left small color="white">mdi-format-list-numbered</v-icon>
            <span class="ml-2">Total Records:</span>
            <span class="font-weight-bold ml-2">{{ tableData.length }}</span>
          </div>
        </v-card>
      </v-col>
      <!-- Show DEPARTMENT only if a department is selected and ALL CLASS -->
      <v-col
        cols="12"
        md="3"
        v-if="selectedDept !== ALL_DEPT && selectedSubDept === ALL_CLASS"
      >
        <v-card flat class="pa-2" color="#00897b" dark>
          <div class="d-flex align-center">
            <v-icon left small color="white">mdi-domain</v-icon>
            <span class="ml-2">{{ deptLabel }}:</span>
            <span class="font-weight-bold ml-2">{{ totalDept }}</span>
          </div>
        </v-card>
      </v-col>
      <!-- Show FILE CLASSIFICATION only if a department and a classification are selected -->
      <v-col
        cols="12"
        md="3"
        v-if="selectedDept !== ALL_DEPT && selectedSubDept !== ALL_CLASS"
      >
        <v-card flat class="pa-2" color="#3949ab" dark>
          <div class="d-flex align-center">
            <v-icon left small color="white">mdi-shape</v-icon>
            <span class="ml-2">{{ subDeptLabel }}:</span>
            <span class="font-weight-bold ml-2">{{ totalSubDept }}</span>
          </div>
        </v-card>
      </v-col>
      <!-- Always show overdue, top insurer, top adjuster -->
      <v-col cols="12" md="2">
        <v-card flat class="pa-2" color="#d32f2f" dark>
          <div class="d-flex align-center">
            <v-icon left small color="white">mdi-alert</v-icon>
            <span class="ml-2">Total Due/Overdue:</span>
            <span class="font-weight-bold ml-2">{{ totalOverdue }}</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="2">
        <v-card flat class="pa-2" color="#ff7043" dark>
          <div class="d-flex align-center">
            <v-icon left small color="white">mdi-account-group</v-icon>
            <span class="ml-2">Top Insurer:</span>
            <span class="font-weight-bold ml-2">{{
              topInsurers.join(", ")
            }}</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="2">
        <v-card flat class="pa-2" color="#8e24aa" dark>
          <div class="d-flex align-center">
            <v-icon left small color="white">mdi-account-tie</v-icon>
            <span class="ml-2">Top Adjuster:</span>
            <span class="font-weight-bold ml-2">{{
              topAdjusters.join(", ")
            }}</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-alert v-else-if="tableGenerated" type="warning" outlined class="mt-4">
      No data found for the selected filters.
    </v-alert>
  </v-card>
</template>
<script>
import moment from "moment";
import { apiConfig } from "../../config/api.config";
import axios from "axios";

// Move constants outside component for template access
const ALL_DEPT = "ALL";
const ALL_CLASS = "ALL";

export default {
  name: "OutstandingMaster",
  data() {
    return {
      selectedDate: null,
      menuDate: false,
      exporting: false,
      loading: false,
      selectedDept: ALL_DEPT,
      selectedSubDept: ALL_CLASS,
      deptOptions: [],
      subDeptOptions: [],
      tableData: [],
      tableGenerated: false,
    };
  },
  computed: {
    ALL_DEPT() {
      return ALL_DEPT;
    },
    ALL_CLASS() {
      return ALL_CLASS;
    },
    totalOverdue() {
      return this.tableData.filter((row) => row.isOverdue).length;
    },
    totalDept() {
      if (this.selectedDept === ALL_DEPT) return this.tableData.length;
      return this.tableData.length;
    },
    totalSubDept() {
      if (this.selectedSubDept === ALL_CLASS || this.selectedDept === ALL_DEPT)
        return this.tableData.length;
      return this.tableData.length;
    },
    deptLabel() {
      if (this.selectedDept === ALL_DEPT) return "";
      const found = this.deptOptions.find((d) => d.id === this.selectedDept);
      return found ? found.name : "Department";
    },
    subDeptLabel() {
      if (this.selectedSubDept === ALL_CLASS || this.selectedDept === ALL_DEPT)
        return "";
      const found = this.subDeptOptions.find(
        (s) => s.id === this.selectedSubDept
      );
      return found ? found.subCode : "File Classification";
    },
    topInsurers() {
      const counts = {};
      this.tableData.forEach((row) => {
        if (!row.insurer) return;
        counts[row.insurer] = (counts[row.insurer] || 0) + 1;
      });
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map((e) => e[0]);
    },
    topAdjusters() {
      const counts = {};
      this.tableData.forEach((row) => {
        if (!row.adjuster) return;
        counts[row.adjuster] = (counts[row.adjuster] || 0) + 1;
      });
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map((e) => e[0]);
    },
    summaryTitle() {
      // Example: "All Departments OUTSTANDING MASTER LIST - 31 August 2025"
      if (!this.selectedDate) return "Outstanding Master List";
      const dept =
        this.selectedDept === ALL_DEPT
          ? "All Departments"
          : this.deptLabel || "Department";
      const dateStr = moment(this.selectedDate).format("DD MMMM YYYY");
      return `${dept} OUTSTANDING MASTER LIST - ${dateStr}`;
    },
  },
  watch: {
    selectedDept(val) {
      if (val === ALL_DEPT) {
        this.selectedSubDept = ALL_CLASS;
        this.subDeptOptions = [
          { id: ALL_CLASS, subCode: "ALL CLASSIFICATION" },
        ];
      } else {
        this.selectedSubDept = ALL_CLASS;
        this.fetchSubDeptOptions(val);
      }
    },
  },
  mounted() {
    this.fetchDeptOptions();
  },
  methods: {
    async fetchDeptOptions() {
      try {
        const res = await axios.get(
          `${apiConfig.baseURL}/compliance/dept-list`
        );
        let depts = res.data || [];
        depts = depts
          .map((d) => ({
            ...d,
            name: (d.name || "").toUpperCase(),
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        this.deptOptions = [{ id: ALL_DEPT, name: "ALL DEPARTMENT" }, ...depts];
      } catch (e) {
        this.deptOptions = [{ id: ALL_DEPT, name: "ALL DEPARTMENT" }];
      }
      this.selectedDept = ALL_DEPT;
      this.selectedSubDept = ALL_CLASS;
      this.subDeptOptions = [{ id: ALL_CLASS, subCode: "ALL CLASSIFICATION" }];
    },
    async fetchSubDeptOptions(deptId) {
      if (!deptId || deptId === ALL_DEPT) {
        this.subDeptOptions = [
          { id: ALL_CLASS, subCode: "ALL CLASSIFICATION" },
        ];
        return;
      }
      try {
        const res = await axios.get(
          `${apiConfig.baseURL}/compliance/subdept-list`,
          {
            params: { deptId },
          }
        );
        let subDepts = res.data || [];
        subDepts = subDepts
          .map((s) => ({
            ...s,
            subCode: (s.subCode || "").toUpperCase(),
          }))
          .sort((a, b) => a.subCode.localeCompare(b.subCode));
        this.subDeptOptions = [
          { id: ALL_CLASS, subCode: "ALL CLASSIFICATION" },
          ...subDepts,
        ];
      } catch (e) {
        this.subDeptOptions = [
          { id: ALL_CLASS, subCode: "ALL CLASSIFICATION" },
        ];
      }
    },
    async generateTable() {
      this.loading = true;
      this.tableGenerated = false;
      this.tableData = [];
      try {
        const params = {
          date: moment(this.selectedDate).format("YYYY-MM-DD"),
        };
        if (this.selectedDept && this.selectedDept !== ALL_DEPT)
          params.deptId = this.selectedDept;
        if (
          this.selectedSubDept &&
          this.selectedSubDept !== ALL_CLASS &&
          this.selectedDept !== ALL_DEPT
        )
          params.subDeptId = this.selectedSubDept;
        const res = await axios.get(
          `${apiConfig.baseURL}/compliance/outstanding-master/table`,
          { params }
        );
        this.tableData = (res.data || []).map((row, idx) => ({
          ...row,
          no: idx + 1,
        }));
      } catch (err) {
        this.tableData = [];
      } finally {
        this.loading = false;
        this.tableGenerated = true;
      }
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const params = {
          date: moment(this.selectedDate).format("YYYY-MM-DD"),
        };
        if (this.selectedDept && this.selectedDept !== ALL_DEPT)
          params.deptId = this.selectedDept;
        if (
          this.selectedSubDept &&
          this.selectedSubDept !== ALL_CLASS &&
          this.selectedDept !== ALL_DEPT
        )
          params.subDeptId = this.selectedSubDept;
        const url =
          `${apiConfig.baseURL}/compliance/outstanding-master/export?` +
          new URLSearchParams(params).toString();
        window.open(url, "_blank");
      } catch (err) {
        this.$emit("error", err.message || "Failed to export Excel");
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
